import { useCallback, useRef } from 'react'
import { useReactFlow, type Node } from 'reactflow'
import { arrayMove } from './arrayMove'
import { type LayoutMode, type GroupNodeData, computeCellSize, computeChildPosition, computeContainerSize, removeChildFromIds } from './GroupNode'
import { getGroupCategory, getMediaCategory } from './groupValidation'
import { computeInsertionIndex } from './groupLayoutUtils'
import { useGroupDropTarget, type RejectionReason } from './GroupDropTargetContext'
import { useCanvasOptional } from '../../CanvasContext'
import { createDebug } from '../../../../utils/debugLog'

const dragLog = createDebug('dw:panel-drag:handler')

/** Distance in pixels from group boundary at which drag-out removal commits */
const DRAG_OUT_THRESHOLD = 100

/**
 * Re-apply grid positions after ReactFlow's internal position commit.
 *
 * ReactFlow applies the final drag position via its internal nodeInternals
 * AFTER our setNodes() callback in onNodeDragStop, overriding grid-snapped
 * positions. The YDoc observer rebuild is also skipped (origin='frontend').
 * This deferred re-snap runs one frame later, after ReactFlow has settled.
 */
function deferredSnapChildren(
  setNodes: (updater: (nds: Node[]) => Node[]) => void,
  childOrder: (string | null)[],
  parentId: string,
  layoutMode: LayoutMode,
  cellW: number,
  cellH: number,
): void {
  requestAnimationFrame(() => {
    dragLog('deferredSnap parent=%s children=%d', parentId, childOrder.filter(c => c !== null).length)
    setNodes((nds) =>
      nds.map((n) => {
        const childIdx = childOrder.indexOf(n.id)
        if (childIdx !== -1 && n.parentNode === parentId) {
          const pos = computeChildPosition(childIdx, layoutMode, cellW, cellH)
          return { ...n, position: pos }
        }
        return n
      }),
    )
  })
}

/**
 * Hook that returns onNodeDrag and onNodeDragStop handlers for ReactFlow.
 * Detects drag-over of standalone canvas nodes onto GroupNodes, providing
 * visual feedback and handling drop absorption.
 *
 * @param existingOnNodeDragStop - The existing onNodeDragStop handler to chain
 * @param canvas - YDoc write functions for persisting group changes (updateNodeData, updateNodeParent, updateNodePosition)
 */
export function useCanvasDragToGroup(
  existingOnNodeDragStop?: (event: React.MouseEvent, node: Node, nodes: Node[]) => void,
  canvas?: {
    updateNodeData: (nodeId: string, updates: Record<string, unknown>) => void
    updateNodeParent: (nodeId: string, parentId: string | null) => void
    updateNodePosition: (nodeId: string, x: number, y: number) => void
  } | null,
) {
  const { setNodes, getNodes } = useReactFlow()
  const dropTargetCtx = useGroupDropTarget()
  const canvasFromContext = useCanvasOptional()
  const resolvedCanvas = canvas ?? canvasFromContext

  // Track whether we're in an active drag-to-group operation
  const activeDragRef = useRef<{ nodeId: string; nodeType: string } | null>(null)

  // Track child drag state for onNodeDragStop to consume
  const childDragStateRef = useRef<{
    sourceGroupId: string
    isOutsideParent: boolean
    targetGroupId: string | null // non-null when hovering a different group
  } | null>(null)

  // Track the last preview insertion index to avoid redundant setNodes calls
  const previewInsertionRef = useRef<number>(-1)

  const onNodeDrag = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      if (!dropTargetCtx) return

      // Skip if this node is a group itself
      if (node.type === 'nodeGroup') return

      const allNodes = getNodes()

      // ── BRANCH: Child node drag (node has parentNode) ──
      if (node.parentNode) {
        // Signal active drag for ghost placeholder (Task 4)
        dropTargetCtx.setActiveDragNodeId(node.id)

        // Look up the parent group node
        const parentGroup = allNodes.find((n) => n.id === node.parentNode)
        if (!parentGroup) return

        const parentGroupData = parentGroup.data as {
          in_child_node_ids?: (string | null)[]
          in_layout_mode?: LayoutMode
        }
        const parentChildIds = parentGroupData.in_child_node_ids || []
        const parentLayoutMode: LayoutMode = parentGroupData.in_layout_mode || 'auto'

        // Compute child's absolute center position
        // (child position is relative to parent in ReactFlow)
        const nodeWidth = node.width || 315
        const nodeHeight = node.height || 300
        const absoluteCenterX = parentGroup.position.x + node.position.x + nodeWidth / 2
        const absoluteCenterY = parentGroup.position.y + node.position.y + nodeHeight / 2

        // Compute parent group's bounding rect
        const { cellWidth: parentCellW, cellHeight: parentCellH } = computeCellSize(
          parentChildIds.filter((id): id is string => id !== null),
          allNodes,
        )
        const { width: parentGroupWidth, height: parentGroupHeight } = computeContainerSize(
          parentChildIds.length,
          parentLayoutMode,
          parentCellW,
          parentCellH,
        )

        const pgx = parentGroup.position.x
        const pgy = parentGroup.position.y

        // Check if child's center is still inside parent group bounds
        const isInsideParent =
          absoluteCenterX >= pgx &&
          absoluteCenterX <= pgx + parentGroupWidth &&
          absoluteCenterY >= pgy &&
          absoluteCenterY <= pgy + parentGroupHeight

        if (isInsideParent) {
          // ── Within-group drag: show insertion indicator ──
          const relativeX = absoluteCenterX - pgx
          const relativeY = absoluteCenterY - pgy

          const { index: insertionIndex, action: dropAction } = computeInsertionIndex(
            relativeX,
            relativeY,
            parentLayoutMode,
            parentCellW,
            parentCellH,
            parentChildIds.length,
            parentChildIds,
          )

          // ── Live reorder preview: reposition siblings to preview positions ──
          const oldIndex = parentChildIds.indexOf(node.id)
          const isFirstDragEvent = childDragStateRef.current === null

          if (isFirstDragEvent || insertionIndex !== previewInsertionRef.current) {
            // Add data-reorder-transition to sibling node wrappers (not the dragged node)
            // for CSS-driven smooth transform animation
            const parentId = node.parentNode!
            for (const childId of parentChildIds) {
              if (childId === null || childId === node.id) continue
              const wrapper = document.querySelector(
                `.react-flow__node[data-id="${childId}"]`,
              ) as HTMLElement | null
              if (wrapper) wrapper.setAttribute('data-reorder-transition', '')
            }

            if (oldIndex !== -1) {
              // Compute preview order using arrayMove
              const previewOrder = arrayMove(parentChildIds, oldIndex, insertionIndex)

              // Reposition siblings to their preview grid positions
              setNodes((nds) =>
                nds.map((n) => {
                  if (n.id === node.id) return n // Don't touch dragged node
                  const previewIdx = previewOrder.indexOf(n.id)
                  if (previewIdx !== -1 && n.parentNode === parentId) {
                    const pos = computeChildPosition(previewIdx, parentLayoutMode, parentCellW, parentCellH)
                    return { ...n, position: pos }
                  }
                  return n
                }),
              )
            }

            previewInsertionRef.current = insertionIndex
          }

          dropTargetCtx.setDropTarget({
            targetGroupId: node.parentNode,
            insertionIndex,
            dropAction,
            rejection: null,
            dragOutState: null,
          })

          activeDragRef.current = { nodeId: node.id, nodeType: node.type || '' }
          childDragStateRef.current = {
            sourceGroupId: node.parentNode,
            isOutsideParent: false,
            targetGroupId: null,
          }
        } else {
          // ── Child is outside parent group ──
          // Check if it's over a different group (for cross-group transfer, Task 5)
          const otherGroups = allNodes.filter(
            (n) => n.type === 'nodeGroup' && n.id !== node.parentNode,
          )

          let foundOtherGroup = false
          for (const group of otherGroups) {
            const groupData = group.data as {
              in_child_node_ids?: (string | null)[]
              in_layout_mode?: LayoutMode
            }
            const childIds = groupData.in_child_node_ids || []
            const layoutMode: LayoutMode = groupData.in_layout_mode || 'auto'
            const { cellWidth, cellHeight } = computeCellSize(
              childIds.filter((id): id is string => id !== null),
              allNodes,
            )
            const { width: groupWidth, height: groupHeight } = computeContainerSize(
              childIds.length,
              layoutMode,
              cellWidth,
              cellHeight,
            )

            const gx = group.position.x
            const gy = group.position.y

            if (
              absoluteCenterX >= gx &&
              absoluteCenterX <= gx + groupWidth &&
              absoluteCenterY >= gy &&
              absoluteCenterY <= gy + groupHeight
            ) {
              // Child is over another group — category validation + insertion index
              const nodeType = node.type || ''
              const nodeCategory = getMediaCategory(nodeType)
              const groupCategory = getGroupCategory(
                childIds.filter((id): id is string => id !== null),
                (nodeId) => allNodes.find((n) => n.id === nodeId)?.type,
              )

              let rejection: RejectionReason = null
              if (nodeCategory === null) {
                rejection = 'unsupported'
              } else if (groupCategory !== null && nodeCategory !== groupCategory) {
                rejection = 'category_mismatch'
              }

              const relativeX = absoluteCenterX - gx
              const relativeY = absoluteCenterY - gy

              const { index: insertionIndex, action: dropAction } = computeInsertionIndex(
                relativeX,
                relativeY,
                layoutMode,
                cellWidth,
                cellHeight,
                childIds.length,
                childIds,
              )

              dropTargetCtx.setDropTarget({
                targetGroupId: group.id,
                insertionIndex,
                dropAction,
                rejection,
                dragOutState: null,
              })

              activeDragRef.current = { nodeId: node.id, nodeType }
              childDragStateRef.current = {
                sourceGroupId: node.parentNode!,
                isOutsideParent: true,
                targetGroupId: group.id,
              }
              foundOtherGroup = true
              break
            }
          }

          if (!foundOtherGroup) {
            // Child is outside parent and not over any other group → drag-out
            // Compute distance from child center to nearest group boundary edge
            const distLeft = pgx - absoluteCenterX
            const distRight = absoluteCenterX - (pgx + parentGroupWidth)
            const distTop = pgy - absoluteCenterY
            const distBottom = absoluteCenterY - (pgy + parentGroupHeight)
            // Take the max of positive distances (how far outside)
            const distance = Math.max(distLeft, distRight, distTop, distBottom, 0)
            const distanceFactor = Math.min(distance / DRAG_OUT_THRESHOLD, 1)

            // Get node name and group name for overlay text
            const nodeName = (node.data as Record<string, unknown>)?.in_name as string
              || (node.data as Record<string, unknown>)?.in_label as string
              || (node.data as Record<string, unknown>)?.label as string
              || node.type || 'Node'
            const parentGroupName = (parentGroup.data as Record<string, unknown>)?.in_label as string
              || (parentGroup.data as Record<string, unknown>)?.label as string
              || 'Group'

            dropTargetCtx.setDropTarget({
              targetGroupId: null,
              insertionIndex: 0,
              dropAction: 'insert-before',
              rejection: null,
              dragOutState: {
                sourceGroupId: node.parentNode!,
                distanceFactor,
                nodeName,
                groupName: parentGroupName,
              },
            })

            activeDragRef.current = { nodeId: node.id, nodeType: node.type || '' }
            childDragStateRef.current = {
              sourceGroupId: node.parentNode!,
              isOutsideParent: true,
              targetGroupId: null,
            }
          }
        }

        return // Don't fall through to standalone logic
      }

      // ── EXISTING: Standalone node drag (no parentNode) ──

      // Find all group nodes on the canvas
      const groupNodes = allNodes.filter((n) => n.type === 'nodeGroup' && n.id !== node.id)

      // Get the dragged node's absolute center position
      const nodeWidth = node.width || 315
      const nodeHeight = node.height || 300
      const nodeCenterX = node.position.x + nodeWidth / 2
      const nodeCenterY = node.position.y + nodeHeight / 2

      // Check each group to see if the node center is within its bounds
      let foundTarget = false
      for (const group of groupNodes) {
        const groupData = group.data as { in_child_node_ids?: (string | null)[]; in_layout_mode?: LayoutMode }
        const childIds = groupData.in_child_node_ids || []
        const layoutMode: LayoutMode = groupData.in_layout_mode || 'auto'
        const { cellWidth, cellHeight } = computeCellSize(
          childIds.filter((id): id is string => id !== null),
          allNodes,
        )
        const { width: groupWidth, height: groupHeight } = computeContainerSize(
          childIds.length,
          layoutMode,
          cellWidth,
          cellHeight,
        )

        // Check if the dragged node's center is within the group's bounding rect
        const gx = group.position.x
        const gy = group.position.y

        if (
          nodeCenterX >= gx &&
          nodeCenterX <= gx + groupWidth &&
          nodeCenterY >= gy &&
          nodeCenterY <= gy + groupHeight
        ) {
          // Node center is over this group — determine acceptance/rejection
          const nodeType = node.type || ''
          const nodeCategory = getMediaCategory(nodeType)
          const groupCategory = getGroupCategory(
            childIds.filter((id): id is string => id !== null),
            (nodeId) => allNodes.find((n) => n.id === nodeId)?.type,
          )

          let rejection: RejectionReason = null
          if (nodeCategory === null) {
            rejection = 'unsupported'
          } else if (groupCategory !== null && nodeCategory !== groupCategory) {
            rejection = 'category_mismatch'
          }

          // Compute pointer position relative to the group
          const relativeX = nodeCenterX - gx
          const relativeY = nodeCenterY - gy

          const { index: insertionIndex, action: dropAction } = computeInsertionIndex(
            relativeX,
            relativeY,
            layoutMode,
            cellWidth,
            cellHeight,
            childIds.length,
            childIds,
          )

          dropTargetCtx.setDropTarget({
            targetGroupId: group.id,
            insertionIndex,
            dropAction,
            rejection,
          })

          activeDragRef.current = { nodeId: node.id, nodeType }
          foundTarget = true
          break // Only one target at a time
        }
      }

      if (!foundTarget) {
        // Not over any group — clear target
        if (dropTargetCtx.dropTarget.targetGroupId !== null) {
          dropTargetCtx.clearDropTarget()
        }
        activeDragRef.current = null
      }
    },
    [getNodes, setNodes, dropTargetCtx],
  )

  const onNodeDragStop = useCallback(
    (event: React.MouseEvent, node: Node, draggedNodes: Node[]) => {
      // ── Within-group reorder (child node dropped back into its parent group) ──
      if (node.parentNode && childDragStateRef.current) {
        const parentGroupId = node.parentNode
        const childState = childDragStateRef.current

        dragLog('dragStop child=%s parent=%s pos=(%d,%d) isOutside=%s targetGroup=%s',
          node.id, parentGroupId, node.position.x, node.position.y,
          childState.isOutsideParent, childState.targetGroupId)

        if (
          dropTargetCtx &&
          dropTargetCtx.dropTarget.targetGroupId === parentGroupId &&
          dropTargetCtx.dropTarget.rejection === null
        ) {
          const allNodes = getNodes()
          const groupNode = allNodes.find((n) => n.id === parentGroupId)
          if (groupNode) {
            const groupData = groupNode.data as GroupNodeData
            const childIds = groupData.in_child_node_ids || []
            const layoutMode: LayoutMode = groupData.in_layout_mode || 'auto'

            const oldIndex = childIds.indexOf(node.id)
            const newIndex = dropTargetCtx.dropTarget.insertionIndex
            const dropAction = dropTargetCtx.dropTarget.dropAction

            if (oldIndex !== -1) {
              if (dropAction === 'replace' && newIndex < childIds.length && childIds[newIndex] === null) {
                // Replace empty slot: move child to the null slot, leave old position as null
                const newOrder = [...childIds]
                newOrder[oldIndex] = null
                newOrder[newIndex] = node.id
                // Trim trailing nulls
                while (newOrder.length > 0 && newOrder[newOrder.length - 1] === null) {
                  newOrder.pop()
                }

                const nonNullIds = newOrder.filter((id): id is string => id !== null)
                const { cellWidth, cellHeight } = computeCellSize(nonNullIds, allNodes)
                const { width, height } = computeContainerSize(
                  newOrder.length,
                  layoutMode,
                  cellWidth,
                  cellHeight,
                )

                setNodes((nds) =>
                  nds.map((n) => {
                    if (n.id === parentGroupId) {
                      return {
                        ...n,
                        data: { ...n.data, in_child_node_ids: newOrder },
                        style: { ...n.style, width, height },
                      }
                    }
                    const childIdx = newOrder.indexOf(n.id)
                    if (childIdx !== -1 && n.parentNode === parentGroupId) {
                      const pos = computeChildPosition(childIdx, layoutMode, cellWidth, cellHeight)
                      return { ...n, position: pos }
                    }
                    return n
                  }),
                )

                resolvedCanvas?.updateNodeData(parentGroupId, { in_child_node_ids: newOrder })
                deferredSnapChildren(setNodes, newOrder, parentGroupId, layoutMode, cellWidth, cellHeight)
                dragLog('replaceSlot child=%s oldIndex=%d newIndex=%d',
                  node.id, oldIndex, newIndex)
              } else if (oldIndex !== newIndex) {
                // Reorder: position actually changed
                const newOrder = arrayMove(childIds, oldIndex, newIndex)

                const nonNullIds = newOrder.filter((id): id is string => id !== null)
                const { cellWidth, cellHeight } = computeCellSize(nonNullIds, allNodes)
                const { width, height } = computeContainerSize(
                  newOrder.length,
                  layoutMode,
                  cellWidth,
                  cellHeight,
                )

                setNodes((nds) =>
                  nds.map((n) => {
                    if (n.id === parentGroupId) {
                      return {
                        ...n,
                        data: { ...n.data, in_child_node_ids: newOrder },
                        style: { ...n.style, width, height },
                      }
                    }
                    const childIdx = newOrder.indexOf(n.id)
                    if (childIdx !== -1) {
                      const pos = computeChildPosition(childIdx, layoutMode, cellWidth, cellHeight)
                      return { ...n, position: pos }
                    }
                    return n
                  }),
                )

                resolvedCanvas?.updateNodeData(parentGroupId, { in_child_node_ids: newOrder })
                deferredSnapChildren(setNodes, newOrder, parentGroupId, layoutMode, cellWidth, cellHeight)
                dragLog('reorder child=%s oldIndex=%d newIndex=%d',
                  node.id, oldIndex, newIndex)
              } else {
                // Same position — snap child back to grid position (no reorder needed)
                const nonNullIds = childIds.filter((id): id is string => id !== null)
                const { cellWidth, cellHeight } = computeCellSize(nonNullIds, allNodes)
                const expectedPos = computeChildPosition(oldIndex, layoutMode, cellWidth, cellHeight)
                dragLog('snapBack child=%s oldIndex=%d dragPos=(%d,%d) gridPos=(%d,%d)',
                  node.id, oldIndex, node.position.x, node.position.y, expectedPos.x, expectedPos.y)
                setNodes((nds) =>
                  nds.map((n) => {
                    const childIdx = childIds.indexOf(n.id)
                    if (childIdx !== -1 && n.parentNode === parentGroupId) {
                      const pos = computeChildPosition(childIdx, layoutMode, cellWidth, cellHeight)
                      return { ...n, position: pos }
                    }
                    return n
                  }),
                )
                deferredSnapChildren(setNodes, childIds, parentGroupId, layoutMode, cellWidth, cellHeight)
              }
            }
          }
        } else if (
          childState.isOutsideParent &&
          childState.targetGroupId &&
          dropTargetCtx &&
          dropTargetCtx.dropTarget.targetGroupId === childState.targetGroupId &&
          dropTargetCtx.dropTarget.rejection === null
        ) {
          // ── Cross-group transfer: child dropped on a compatible different group ──
          const allNodes = getNodes()
          const sourceGroupId = childState.sourceGroupId
          const targetGId = childState.targetGroupId
          const crossInsertionIndex = dropTargetCtx.dropTarget.insertionIndex

          const sourceGroup = allNodes.find((n) => n.id === sourceGroupId)
          const targetGroup = allNodes.find((n) => n.id === targetGId)
          if (sourceGroup && targetGroup) {
            const sourceData = sourceGroup.data as {
              in_child_node_ids?: (string | null)[]
              in_layout_mode?: LayoutMode
            }
            const targetData = targetGroup.data as {
              in_child_node_ids?: (string | null)[]
              in_layout_mode?: LayoutMode
            }

            const sourceChildIds = sourceData.in_child_node_ids || []
            const targetChildIds = targetData.in_child_node_ids || []

            // 1. Remove from source (always leaves gap)
            const newSourceIds = removeChildFromIds(node.id, sourceChildIds)

            // 2. Insert into target at the insertion index from drop target context
            const newTargetIds = [...targetChildIds]
            newTargetIds.splice(crossInsertionIndex, 0, node.id)

            // 3. Compute layout for source group
            const sourceLayoutMode: LayoutMode = sourceData.in_layout_mode || 'auto'
            const sourceNonNullIds = newSourceIds.filter((id): id is string => id !== null)
            const { cellWidth: srcCellW, cellHeight: srcCellH } = computeCellSize(sourceNonNullIds, allNodes)
            const { width: srcWidth, height: srcHeight } = computeContainerSize(
              newSourceIds.length,
              sourceLayoutMode,
              srcCellW,
              srcCellH,
            )

            // 4. Compute layout for target group
            const targetLayoutMode: LayoutMode = targetData.in_layout_mode || 'auto'
            const targetNonNullIds = newTargetIds.filter((id): id is string => id !== null)
            const { cellWidth: tgtCellW, cellHeight: tgtCellH } = computeCellSize(targetNonNullIds, allNodes)
            const { width: tgtWidth, height: tgtHeight } = computeContainerSize(
              newTargetIds.length,
              targetLayoutMode,
              tgtCellW,
              tgtCellH,
            )

            // 5. Atomic setNodes: update both groups + transferred child + reposition all children
            setNodes((nds) =>
              nds.map((n) => {
                // Source group: update in_child_node_ids + resize container
                if (n.id === sourceGroupId) {
                  return {
                    ...n,
                    data: { ...n.data, in_child_node_ids: newSourceIds },
                    style: { ...n.style, width: srcWidth, height: srcHeight },
                  }
                }
                // Target group: update in_child_node_ids + resize container
                if (n.id === targetGId) {
                  return {
                    ...n,
                    data: { ...n.data, in_child_node_ids: newTargetIds },
                    style: { ...n.style, width: tgtWidth, height: tgtHeight },
                  }
                }
                // Transferred child: update parentNode + position in target group's grid
                if (n.id === node.id) {
                  const pos = computeChildPosition(crossInsertionIndex, targetLayoutMode, tgtCellW, tgtCellH)
                  return { ...n, parentNode: targetGId, position: pos }
                }
                // Reposition remaining source group children
                const srcIdx = newSourceIds.indexOf(n.id)
                if (srcIdx !== -1) {
                  const pos = computeChildPosition(srcIdx, sourceLayoutMode, srcCellW, srcCellH)
                  return { ...n, position: pos }
                }
                // Reposition target group children (excluding transferred child, handled above)
                const tgtIdx = newTargetIds.indexOf(n.id)
                if (tgtIdx !== -1 && n.id !== node.id) {
                  const pos = computeChildPosition(tgtIdx, targetLayoutMode, tgtCellW, tgtCellH)
                  return { ...n, position: pos }
                }
                return n
              }),
            )

            // 6. Yjs persistence for both groups' in_child_node_ids
            resolvedCanvas?.updateNodeData(sourceGroupId, { in_child_node_ids: newSourceIds })
            resolvedCanvas?.updateNodeData(targetGId, { in_child_node_ids: newTargetIds })

            // 7. Yjs persistence for transferred child's parentNode
            resolvedCanvas?.updateNodeParent(node.id, targetGId)
          }
        } else if (childState.isOutsideParent && !childState.targetGroupId) {
          // ── Drag-out removal: outside parent, no cross-group target ──
          const allNodes = getNodes()
          const sourceGroup = allNodes.find((n) => n.id === childState.sourceGroupId)
          if (sourceGroup) {
            const sourceData = sourceGroup.data as GroupNodeData
            const sourceChildIds = sourceData.in_child_node_ids || []
            const layoutMode: LayoutMode = sourceData.in_layout_mode || 'auto'

            // 1. Remove child from in_child_node_ids (always leaves gap)
            const newChildIds = removeChildFromIds(node.id, sourceChildIds)

            // 2. Compute absolute canvas position
            const absoluteX = sourceGroup.position.x + node.position.x
            const absoluteY = sourceGroup.position.y + node.position.y

            // 3. Compute layout for remaining children
            const nonNullIds = newChildIds.filter((id): id is string => id !== null)
            const { cellWidth, cellHeight } = computeCellSize(nonNullIds, allNodes)
            const { width, height } = computeContainerSize(
              nonNullIds.length > 0 ? newChildIds.length + 1 : 0,
              layoutMode,
              cellWidth,
              cellHeight,
            )

            // 4. Atomic setNodes: detach child, update group, reposition remaining
            setNodes((nds) =>
              nds.map((n) => {
                if (n.id === node.id) {
                  return {
                    ...n,
                    parentNode: undefined,
                    extent: undefined,
                    position: { x: absoluteX, y: absoluteY },
                  }
                }
                if (n.id === childState.sourceGroupId) {
                  return {
                    ...n,
                    data: { ...n.data, in_child_node_ids: newChildIds },
                    style: { ...n.style, width, height },
                  }
                }
                const childIdx = newChildIds.indexOf(n.id)
                if (childIdx !== -1) {
                  const pos = computeChildPosition(childIdx, layoutMode, cellWidth, cellHeight)
                  return { ...n, position: pos }
                }
                return n
              }),
            )

            // 5. Persist to Y doc
            resolvedCanvas?.updateNodeData(childState.sourceGroupId, { in_child_node_ids: newChildIds })
            resolvedCanvas?.updateNodeParent(node.id, null)
            resolvedCanvas?.updateNodePosition(node.id, absoluteX, absoluteY)

            // 6. Trigger "Removed..." flash
            const nodeName = (node.data as Record<string, unknown>)?.in_name as string
              || (node.data as Record<string, unknown>)?.in_label as string
              || (node.data as Record<string, unknown>)?.label as string
              || node.type || 'Node'
            const groupName = (sourceGroup.data as Record<string, unknown>)?.in_label as string
              || (sourceGroup.data as Record<string, unknown>)?.label as string
              || 'Group'

            dropTargetCtx?.setRemovedFlash({
              sourceGroupId: childState.sourceGroupId,
              nodeName,
              groupName,
            })
          }
        }

        childDragStateRef.current = null
        previewInsertionRef.current = -1
        activeDragRef.current = null

        // Clean up data-reorder-transition from all child node wrappers
        document
          .querySelectorAll('.react-flow__node[data-reorder-transition]')
          .forEach((el) => el.removeAttribute('data-reorder-transition'))

        dropTargetCtx?.clearDropTarget()
        dropTargetCtx?.setActiveDragNodeId(null)
        dragLog('chainToBase child=%s pos=(%d,%d) — base handler will write this position to YDoc',
          node.id, node.position.x, node.position.y)
        existingOnNodeDragStop?.(event, node, draggedNodes)
        return
      }

      if (dropTargetCtx && dropTargetCtx.dropTarget.targetGroupId && activeDragRef.current) {
        const { targetGroupId, insertionIndex, dropAction, rejection } = dropTargetCtx.dropTarget

        // Only absorb if not rejected
        if (rejection === null) {
          const allNodes = getNodes()
          const groupNode = allNodes.find((n) => n.id === targetGroupId)
          if (groupNode) {
            const groupData = groupNode.data as { in_child_node_ids?: (string | null)[]; in_layout_mode?: LayoutMode }
            const childIds = groupData.in_child_node_ids || []
            const layoutMode: LayoutMode = groupData.in_layout_mode || 'auto'

            // Apply the drop action
            const newIds = [...childIds]
            if (dropAction === 'replace' && insertionIndex < newIds.length && newIds[insertionIndex] === null) {
              // Replace empty slot
              newIds[insertionIndex] = node.id
            } else {
              // Insert before/after
              newIds.splice(insertionIndex, 0, node.id)
            }

            // Compute cell size with the new child included
            const nonNullIds = newIds.filter((id): id is string => id !== null)
            const { cellWidth, cellHeight } = computeCellSize(nonNullIds, allNodes)

            // Atomic setNodes: set parentNode on dragged node, reposition all children,
            // update group's in_child_node_ids and container size
            setNodes((nds) =>
              nds.map((n) => {
                // Set parentNode on dragged node and position it in the grid
                if (n.id === node.id) {
                  const pos = computeChildPosition(insertionIndex, layoutMode, cellWidth, cellHeight)
                  return { ...n, parentNode: targetGroupId, position: pos }
                }
                // Update group node data
                if (n.id === targetGroupId) {
                  return { ...n, data: { ...n.data, in_child_node_ids: newIds } }
                }
                // Reposition other children in the group
                const childIdx = newIds.indexOf(n.id)
                if (childIdx !== -1 && n.id !== node.id && n.parentNode === targetGroupId) {
                  const pos = computeChildPosition(childIdx, layoutMode, cellWidth, cellHeight)
                  return { ...n, position: pos }
                }
                return n
              }),
            )

            // Dual-write: persist in_child_node_ids and parentNode to Y doc
            resolvedCanvas?.updateNodeData(targetGroupId, { in_child_node_ids: newIds })
            resolvedCanvas?.updateNodeParent(node.id, targetGroupId)

            // Clear the drop target
            dropTargetCtx.clearDropTarget()
            activeDragRef.current = null

            // Don't call existing handler since we handled the drop
            return
          }
        }

        // If rejected, clear and fall through to existing handler
        dropTargetCtx.clearDropTarget()
        activeDragRef.current = null
      }

      // Chain to existing handler for normal drag stops
      existingOnNodeDragStop?.(event, node, draggedNodes)
    },
    [dropTargetCtx, getNodes, setNodes, resolvedCanvas, existingOnNodeDragStop],
  )

  return { onNodeDrag, onNodeDragStop }
}
